import { Resolution } from "@/sdk"
import * as gbk from "gbk.js"
import { Message, md5 } from "js-md5"
import UAParser from "ua-parser-js"

type DebounceFunc = (this: any, ...args: any[]) => void

// 背压函数，防抖
export function debounce(fn: DebounceFunc, ms = 300): DebounceFunc {
  let timeoutId: ReturnType<typeof setTimeout>
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      fn.apply(this, args)
    }, ms)
  }
}

// 计算传入数据的MD5值
export function calculateMD5(value: Message): string {
  return md5(value)
}

export type Encoder = "UTF-8" | "UTF-16" | "GBK"

export function string2Buffer(
  str: string,
  encoder: Encoder = "UTF-8"
): Uint8Array {
  switch (encoder) {
    case "UTF-8": {
      // 返回 UTF-8 编码的数据
      const buffer = new TextEncoder().encode(str)
      return new Uint8Array(buffer)
    }
    case "UTF-16": {
      // 返回 UTF-16 LE 编码的数据
      const buffer = new ArrayBuffer(str.length * 2)
      const utf16View = new DataView(buffer)
      for (let i = 0; i < str.length; i++) {
        // 依次写入字符的 UTF-16 编码(小端序)
        utf16View.setUint16(i * 2, str.charCodeAt(i))
      }
      return new Uint8Array(buffer)
    }
    case "GBK": {
      return new Uint8Array(gbk.encode(str))
    }
  }
}

export function buffer2String(
  data: Uint8Array,
  encoder: Encoder = "UTF-8"
): string {
  switch (encoder) {
    case "UTF-8": {
      return new TextDecoder().decode(data)
    }
    case "UTF-16": {
      return new TextDecoder("utf-16").decode(data)
    }
    case "GBK": {
      return gbk.decode(Array.from(data))
    }
  }
}

/**
 * 保留小数位数(四舍五入)，并去除尾部多余的0
 * @param num
 * @param decimal 要保留的小数位数，默认为2
 */
export function keepPrecision(num: number, decimal = 2): string {
  // return num.toFixed(decimal).replace(/(\.\d*?[1-9])0+$/, "$1")
  let res = num.toFixed(decimal)
  while (res.endsWith("0") || res.endsWith(".")) {
    res = res.slice(0, -1)
  }
  return res
}

// 用于显示文件大小
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`
  }
  const kb = bytes / 1024
  if (kb < 1000) {
    return `${Math.round(kb)}KB`
  }
  const mb = kb / 1024
  if (mb < 1000) {
    return `${keepPrecision(mb, 1)}MB`
  }
  const gb = mb / 1024
  return `${keepPrecision(gb, 1)}GB`
}

export function clamp(num: number, min: number, max: number): number {
  if (num < min) return min
  if (num > max) return max
  return num
}

export function assert(condition: boolean, message: string): asserts condition {
  if (!condition) {
    throw new Error(message)
  }
}

// 当前页面是否有交互
export function isUserActive() {
  return (navigator as any).userActivation.isActive
}

// 将“1900 x 1080”这种格式的字符串解析为分辨率对象{width: 1900, height: 1080}}
export function parseResolution(resolutionString: string): Resolution | null {
  const parts = resolutionString.split(" x ")

  if (parts.length !== 2) {
    return null
  }

  // 将字符串转换为数字
  const width = parseInt(parts[0], 10)
  const height = parseInt(parts[1], 10)

  return { width, height }
}

// 判断是否为Chromium的Blink内核的浏览器
export function isChromiumBlink(): string {
  const { browser } = UAParser(window.navigator.userAgent)
  console.log("browser type:", browser)
  const browserName = browser.name ?? ""

  return browserName
}

export const mytest = true

// ============================================================
/**
 * 校验11位中国手机号
 * @param phone 手机号码字符串
 * @returns 是否为有效的中国手机号
 */
export function validateChinesePhone(phone: string): boolean {
  // 基础校验：非空、11位、全数字
  if (
    !phone ||
    typeof phone !== "string" ||
    phone.length !== 11 ||
    !/^\d+$/.test(phone)
  ) {
    return false
  }

  // 中国手机号正则表达式（覆盖所有当前有效号段）
  const chinaMobileRegex =
    /^1(3\d|4[5-9]|5[0-35-9]|6[2567]|7[0-8]|8\d|9[0-35-9])\d{8}$/
  return chinaMobileRegex.test(phone)
}

// 使用公钥加密密码
/**
 * 使用从接口获取的公钥加密数据，并返回Vault格式的密文
 * @param {string} plaintext - 要加密的明文数据
 * @param {Object} publicKeyObj - 接口返回的响应数据，包含公钥和版本信息
 * @returns {Promise<string>} 返回Vault格式的密文字符串
 */
async function importPublicKey(pem: string) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s/g, "")
  const binaryDer = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
  return await crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"]
  )
}
async function encryptData(publicKey: CryptoKey, data: string) {
  const encoder = new TextEncoder()
  const encoded = encoder.encode(data)
  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    encoded
  )
  return btoa(String.fromCharCode(...new Uint8Array(encrypted)))
}

export async function encryptToVaultFormat(
  plaintext: string,
  publicKeyObj: { public_key: string; version: number }
): Promise<string> {
  // 拼接 PEM 格式
  const publicKeyPem = `-----BEGIN PUBLIC KEY-----\n${(publicKeyObj?.public_key?.match(/.{1,64}/g) ?? []).join("\n")}\n-----END PUBLIC KEY-----`
  const keyVersion = publicKeyObj.version

  try {
    const publicKey = await importPublicKey(publicKeyPem)
    const encrypted = await encryptData(publicKey, plaintext)

    // 拼接 Vault 所需格式 v<version>:<密文>
    return `v${keyVersion}:${encrypted}`
  } catch (error) {
    console.error("加密过程中发生错误:", error)
    throw error
  }
}
